// components/blog/shared/InteractiveElements.tsx - Client component for interactive blog elements

'use client'

import { usePathname } from 'next/navigation'
import Link from 'next/link'
import RiArrowRightSLine from '~/icons/remix/generated/RiArrowRightSLine'
import RiHome4Line from '~/icons/remix/generated/RiHome4Line'
import RiSearch2Line from '~/icons/remix/generated/RiSearch2Line'
import RiMenuLine from '~/icons/remix/generated/RiMenuLine'
import RiArticleLine from '~/icons/remix/generated/RiArticleLine'
import RiFolder2Line from '~/icons/remix/generated/RiFolder2Line'
import { motion, AnimatePresence } from 'motion/react'
import useScroll from '~/lib/use-scroll'
import { ShareWrapper } from '~/components/blog/features/ShareFeatures/ShareWrapper'
import { useMemo, useState, useEffect, useCallback } from 'react'
import { cn } from '~/lib/utils'
import { generateBreadcrumbs, type Breadcrumb } from '~/components/blog/shared/blog-utils'

/**
 * Props for the InteractiveElements component
 * 
 * @interface InteractiveElementsProps
 * @property {boolean} showNavbar - Whether to show the navigation header
 * @property {boolean} showSearch - Whether to show search and filter controls
 */
interface InteractiveElementsProps {
  /** Whether to show the navigation header */
  showNavbar: boolean
  /** Whether to show the search control */
  showSearch: boolean
}

/**
 * Interactive elements component with header, breadcrumbs, search, filters, and share functionality
 * Handles scroll-based visibility, mobile navigation, and client-side routing
 * 
 * @param {InteractiveElementsProps} props - Component props
 * @param {boolean} showNavbar - Whether to show the navigation header
 * @param {boolean} showSearch - Whether to show search and filter controls
 * @returns {JSX.Element} The interactive elements component
 */
export function InteractiveElements({ 
  showNavbar,
  showSearch
}: InteractiveElementsProps): JSX.Element {
  const pathname = usePathname()
  const scrolled = useScroll(50)
  
  // State management with proper initial values to prevent hydration issues
  const [showMobileNav, setShowMobileNav] = useState<boolean>(false)
  const [mounted, setMounted] = useState<boolean>(false)

  // Handle mounting to prevent hydration mismatches
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot SSR hydration flag, set once after mount (client-only)
    setMounted(true)
  }, [])

  // Close mobile nav on route changes
  useEffect(() => {
    if (mounted) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional client-side state update inside an effect (SSR-safe staging of derived UI state)
      setShowMobileNav(false)
    }
  }, [pathname, mounted])

  // Generate breadcrumbs from the current pathname. Deliberately NOT gated on
  // `mounted`: `generateBreadcrumbs` is a pure, synchronous function of the
  // pathname and `usePathname()` returns the same value during SSR and
  // hydration, so server and client markup are identical (no mismatch) and the
  // breadcrumb anchor tags ship in the first-pass HTML for crawlers/link
  // equity instead of appearing only after hydration.
  const breadcrumbs = useMemo<Breadcrumb[]>(() => {
    if (!pathname) {
      return []
    }
    return generateBreadcrumbs(pathname)
  }, [pathname])

  // Article pages own sharing because they have the authored post metadata.
  // Keep the layout control only for listing pages.
  const shareTitle = useMemo<string | undefined>(() => {
    if (breadcrumbs.length > 1 && breadcrumbs.at(-1)?.type !== 'article') {
      return breadcrumbs[breadcrumbs.length - 1]?.label
    }
    return undefined
  }, [breadcrumbs])

  // Memoized event handlers to prevent unnecessary re-renders
  const toggleMobileNav = useCallback(() => {
    setShowMobileNav(prev => !prev)
  }, [])

  const closeMobileNav = useCallback(() => {
    setShowMobileNav(false)
  }, [])

  return (
    <>
      {/* Header */}
      {showNavbar && (
        <header className="fixed inset-x-0 top-0 z-40 h-[152px] pt-24 transition-all duration-300">
          {/* Animated Background Layer */}
          <div 
            className={cn(
              "absolute inset-0 pointer-events-none transition-all duration-300 ease-in-out",
              scrolled ? "opacity-100" : "opacity-0",
              "bg-gradient-to-b from-white via-white/95 to-white/30 dark:from-gray-900 dark:via-gray-900/95 dark:to-gray-900/30",
              "backdrop-blur-md",
              "border-b border-gray-200/60 dark:border-gray-800/60",
              "shadow-md"
            )}
          />

          <div className="mx-auto max-w-7xl relative">
            {/* Interactive Navigation */}
            <motion.nav
              initial={{ opacity: 0, y: -10 }}
              animate={{ 
                opacity: scrolled ? 1 : 0,
                y: scrolled ? 0 : -10,
                pointerEvents: scrolled ? 'auto' : 'none'
              }}
              transition={{ duration: 0.2 }}
              className="flex h-14 items-center justify-between px-4 sm:px-6 lg:px-8"
              role="navigation"
              aria-label="Blog navigation"
            >
              {/* Mobile Navigation Toggle */}
              <div className="flex items-center gap-2 md:hidden">
                <button
                  type="button"
                  onClick={toggleMobileNav}
                  className={cn(
                    "relative z-50 inline-flex items-center rounded-lg p-2 text-sm transition-all duration-200",
                    "bg-white/50 hover:bg-white dark:bg-gray-800/50 dark:hover:bg-gray-800",
                    "border border-gray-200/30 dark:border-gray-700/30",
                    "text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white",
                    "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900",
                    showMobileNav && "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                  )}
                  aria-expanded={showMobileNav}
                  aria-controls="mobile-navigation"
                  aria-label={showMobileNav ? "Close navigation menu" : "Open navigation menu"}
                >
                  <RiMenuLine className="h-5 w-5" aria-hidden="true" />
                </button>
                <Link
                  href="/blog"
                  className={cn(
                    "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300",
                    "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 rounded-sm"
                  )}
                  aria-label="Go to blog home"
                >
                  <RiHome4Line className="h-5 w-5" aria-hidden="true" />
                </Link>
              </div>

              {/* Desktop Breadcrumbs — rendered on the server too (no
                  `mounted` gate): `showMobileNav` starts false on both server
                  and client, so SSR and hydration markup match while the
                  crumb anchors land in the first-pass HTML. */}
              {!showMobileNav && (
                <nav aria-label="Breadcrumb">
                  <ol className="hidden md:flex items-center space-x-2 overflow-x-auto hide-scrollbar z-50">
                    <li>
                      <Link
                        href="/blog"
                        className={cn(
                          "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300",
                          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 rounded-sm"
                        )}
                        aria-label="Go to blog home"
                      >
                        <RiHome4Line className="h-5 w-5" aria-hidden="true" />
                      </Link>
                    </li>
                    {breadcrumbs.map((breadcrumb, index) => (
                      <li key={`${breadcrumb.href}-${index}`} className="flex items-center">
                        <RiArrowRightSLine className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                        <Link
                          href={breadcrumb.href}
                          className={cn(
                            "ml-2 text-sm font-medium whitespace-nowrap max-w-[275px] overflow-hidden overflow-ellipsis",
                            "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 rounded-sm",
                            breadcrumb.current
                              ? "text-blue-600 dark:text-blue-400"
                              : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                          )}
                          aria-current={breadcrumb.current ? 'page' : undefined}
                        >
                          {breadcrumb.label}
                        </Link>
                      </li>
                    ))}
                  </ol>
                </nav>
              )}

              {/* Search */}
              {showSearch && (
                <div className="flex items-center gap-2 z-50">
                  <Link
                    href="/blog/search"
                    className={cn(
                      "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200",
                      "bg-white/50 hover:bg-white dark:bg-gray-800/50 dark:hover:bg-gray-800",
                      "border border-gray-200/30 dark:border-gray-700/30",
                      "text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white",
                      "shadow-sm hover:shadow",
                      "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                    )}
                    aria-label="Search blog posts"
                  >
                    <RiSearch2Line className="h-4 w-4" aria-hidden="true" />
                    <span className="hidden sm:inline">Search</span>
                  </Link>
                </div>
              )}
            </motion.nav>

            {/* Animated Mobile Navigation & Filters */}
            {mounted && (
              <>
                <AnimatePresence mode="wait">
                  {showMobileNav && (
                    <motion.div
                      id="mobile-navigation"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      style={{ zIndex: 45 }}
                      className={cn(
                        "md:hidden fixed inset-x-0 top-[152px] mx-4",
                        "bg-white/95 dark:bg-gray-800/95",
                        "backdrop-blur-md rounded-lg",
                        "border border-gray-200/30 dark:border-gray-700/30",
                        "shadow-lg max-h-[calc(100vh-180px)] overflow-y-auto"
                      )}
                      role="dialog"
                      aria-modal="true"
                      aria-label="Mobile navigation menu"
                    >
                      <nav className="p-4" role="navigation" aria-label="Mobile breadcrumb navigation">
                        <div className="space-y-2">
                          {breadcrumbs.map((breadcrumb, index) => (
                            <Link
                              key={`mobile-${breadcrumb.href}-${index}`}
                              href={breadcrumb.href}
                              onClick={closeMobileNav}
                              className={cn(
                                "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors duration-200",
                                "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800",
                                breadcrumb.current
                                  ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50",
                                breadcrumb.type === 'category' && "font-medium",
                                breadcrumb.type === 'subcategory' && "ml-2",
                                breadcrumb.type === 'article' && "ml-3"
                              )}
                              aria-current={breadcrumb.current ? 'page' : undefined}
                            >
                              {breadcrumb.type === 'category' && <RiFolder2Line className="h-4 w-4 opacity-75" aria-hidden="true" />}
                              {breadcrumb.type === 'subcategory' && <RiFolder2Line className="h-4 w-4 opacity-50" aria-hidden="true" />}
                              {breadcrumb.type === 'article' && <RiArticleLine className="h-4 w-4 opacity-25" aria-hidden="true" />}
                              <span className="truncate max-w-[275px]">{breadcrumb.label}</span>
                            </Link>
                          ))}
                        </div>
                      </nav>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}
          </div>
        </header>
      )}

      {/* Share Button */}
      {shareTitle && mounted && (
        <ShareWrapper title={shareTitle} />
      )}
    </>
  )
}
